import { EApplicantType, EDocumentStatus, EDocumentType } from '@if/core/types'
import { ApplicantDto } from '@src/types/CreditApplicationSchema'
import { RequiredDocument } from '@src/types/RequiredDocument'
import { hasRequiredDocuments } from '@src/data/income-selectors'
import { useMemo } from 'react'

export const useHasRequiredDocuments = (
  requiredDocuments: RequiredDocument[],
  documentTypes: (number | string)[],
  applicantType: EApplicantType,
  status: EDocumentStatus,
): boolean =>
  useMemo(
    () => hasRequiredDocuments(requiredDocuments, documentTypes, applicantType, status),
    [applicantType, documentTypes, requiredDocuments, status],
  )

export const useHasBankStatementOrVoidCheque = (
  requiredDocuments: RequiredDocument[],
  applicantType: EApplicantType,
): boolean => {
  const hasBankStatementOrVoidCheque: boolean = useMemo(
    () =>
      requiredDocuments.some(
        (d) =>
          (d.typeId === EDocumentType.VoidCheque || d.typeId === EDocumentType.SixMonthsPersonalBankStatements) &&
          d.applicantType === applicantType,
      ),
    [applicantType, requiredDocuments],
  )

  return hasBankStatementOrVoidCheque
}

export const getVerifiedIncome = (
  hasBankStatementOrVoidCheque: boolean,
  hasIncomeDocumentsApproved: boolean,
  applicant: ApplicantDto,
): number => {
  if (hasBankStatementOrVoidCheque && applicant.incomeVerification?.incomeMismatch && !hasIncomeDocumentsApproved) {
    return applicant.incomeVerification.thirdPartyIncome
  }
  return applicant.incomeVerification!.declaredIncome
}

export const useCanContinueWithComputedIncome = (applicant: ApplicantDto) => {
  return useMemo<boolean>(
    () =>
      applicant.incomeVerification!.thirdPartyIncome >= 21000 &&
      applicant.incomeVerification!.thirdPartyIncome / applicant.incomeVerification!.declaredIncome >= 0.5 &&
      !applicant.incomeVerification!.nameMismatch,
    [applicant],
  )
}
